import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BroadcastsService } from '../../services/broadcasts.service';
import { ClarityModule, ClrIconModule } from '@clr/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Broadcast } from '../../../../models/broadcast';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { DetailsComponent } from '../details/details.component';
import { VoxtralScheduleDialogComponent } from '../voxtral-schedule-dialog/voxtral-schedule-dialog.component';
import { OverlayPanelComponent } from '../../../../shared/components/overlay-panel/overlay-panel.component';
import { LogEventsService } from '../../services/log-events.service';
import { Subscription, finalize } from 'rxjs';
import { LogEvent } from '../../../../models/log-event';
import { WebrtcAudioService, WebrtcChannel } from '../../services/webrtc-audio.service';
import { environment } from '../../../../../environments/environment';
import { CHANNEL_ORDER, CHANNEL_ORDER_CSV, channelSortIndex } from '../../channel-order';
import { ChannelColumn, buildChannelColumns, programmtagDateFromKey } from './programplan-layout';
import { HasPermissionDirective } from '../../../../../security/has-permission.directive';

@Component({
  selector: 'app-programplan',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, ClrIconModule, DetailsComponent, VoxtralScheduleDialogComponent, OverlayPanelComponent, HasPermissionDirective],
  providers: [{ provide: LOCALE_ID, useValue: 'de-DE' }],
  templateUrl: './programplan.component.html',
  styleUrl: './programplan.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ProgramplanComponent implements OnInit, AfterViewInit, OnDestroy {
  private logEventsSub!: Subscription;
  private webrtcSub!: Subscription;
  private broadcastsSub!: Subscription;
  private connectionRestoredSub!: Subscription;
  @ViewChild('webrtcAudio') webrtcAudioRef!: ElementRef<HTMLAudioElement>;

  selectedDate: Date = new Date();
  // Programmtag axis 06:00 → 05:59(+1). The backend's ProgrammDate query
  // returns exactly this window, so 24 hourly rows cover every row;
  // early-morning next-day slots wrap to the bottom (programmtagStartMinute).
  hours = Array.from({ length: 24 }, (_, i) => (i + 6) % 24);
  broadcasts: Broadcast[] = [];
  columns: ChannelColumn[] = [];
  visibleBroadcastsCount = 0;
  selectedChannels: string[] = [];
  showLive: boolean = true;
  showVOD: boolean = true;
  isLoading: boolean = true;
  selectedBroadcast: Broadcast|any = null;
  popupPosition = { top: 0, left: 0 };
  detailsVisible = false;
  detailsBroadcast: any = null;

  // WebRTC audio player
  webrtcChannels: WebrtcChannel[] = [];
  selectedWebrtcChannel: string = '';
  isWebrtcPlaying: boolean = false;
  isWebrtcConnecting: boolean = false;
  webrtcState: string = 'disconnected';
  private webrtcRefreshInterval: ReturnType<typeof setInterval> | null = null;

  voxtralResetAllInProgress = false;

  voxtralScheduleDialogVisible = false;
  voxtralScheduleChannel: string = '';

  constructor(
    private http: HttpClient,
    private broadcastsService: BroadcastsService,
    private logEventsService: LogEventsService,
    private webrtcAudioService: WebrtcAudioService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Load WebRTC channels and refresh every 60s
    this.refreshWebrtcChannels();
    this.webrtcRefreshInterval = setInterval(() => this.refreshWebrtcChannels(), 60000);

    this.webrtcSub = this.webrtcAudioService.connectionState$.subscribe((state: string) => {
      this.webrtcState = state;
      this.isWebrtcConnecting = state === 'connecting';
      this.isWebrtcPlaying = state === 'connected';
      if (state === 'error' || state === 'disconnected') {
        this.isWebrtcPlaying = false;
        this.isWebrtcConnecting = false;
      }
      // Mirror the service-managed session into the DDL so an operator who
      // pressed Play in the details panel can also press Stop here.
      const activeId = this.webrtcAudioService.activeSessionId;
      if (state === 'connecting' || state === 'connected') {
        if (activeId && activeId !== this.selectedWebrtcChannel) {
          this.selectedWebrtcChannel = activeId;
          // Make sure the option exists in the DDL — the periodic refresh
          // could be up to 60s out of date when details starts a new session.
          if (!this.webrtcChannels.some(c => c.sessionId === activeId)) {
            this.refreshWebrtcChannels();
          }
        }
      }
      this.cdr.detectChanges();
    });

    this.logEventsSub = this.logEventsService.getLogEvents().subscribe({
      next: (logEvent: LogEvent) => { 
//        console.log('LogEvent received:', logEvent, logEvent.tags);
        if( logEvent.tags?.includes('LIVE') && logEvent.tags?.includes('Status') ){
          const matchingBroadcast = this.broadcasts.find(broadcast =>
            logEvent.tags?.includes(broadcast.key)
          );
          console.log('Matching broadcast:', matchingBroadcast);
          if (matchingBroadcast) {
            matchingBroadcast.instanceInitializing = logEvent.tags?.includes('instanceInitializing');
            if( this.detailsBroadcast ) {
              this.detailsBroadcast.instanceInitializing = matchingBroadcast.instanceInitializing;
            }
            this.calculatePositions();
            this.cdr.detectChanges(); 
          }
        }
      }
    });

    // ONE subscription for the component's lifetime. Previously this was
    // (re-)subscribed inside loadData() on every date change without ever
    // unsubscribing — handlers stacked and each event was processed N times.
    this.broadcastsSub = this.broadcastsService.subscribeToBroadcasts().subscribe({
      next: (result) => this.onBroadcastChanged(result),
      error: (error) => console.error('Fehler beim Abonnieren der Daten:', error),
    });

    // After a WebSocket outage the backend publisher's cursor has moved on —
    // events (incl. delete tombstones) sent meanwhile are gone for this
    // client. Reload the day once data flows again.
    this.connectionRestoredSub = this.broadcastsService.connectionRestored$.subscribe(() => {
      this.loadData(this.selectedDate);
    });

    this.loadData(new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate())), true);
  }

  ngAfterViewInit(): void {
    if (this.webrtcAudioRef?.nativeElement) {
      this.webrtcAudioService.setAudioElement(this.webrtcAudioRef.nativeElement);
    }
  }

  ngOnDestroy() {
    this.logEventsSub.unsubscribe();
    this.webrtcSub?.unsubscribe();
    this.broadcastsSub?.unsubscribe();
    this.connectionRestoredSub?.unsubscribe();
    this.webrtcAudioService.disconnect();
    if (this.webrtcRefreshInterval) {
      clearInterval(this.webrtcRefreshInterval);
    }
  }

  private refreshWebrtcChannels(): void {
    this.webrtcAudioService.fetchSessions().then(channels => {
      this.webrtcChannels = channels;
      this.cdr.detectChanges();
    });
  }

  onWebrtcPlay(): void {
    if (!this.selectedWebrtcChannel || !this.webrtcAudioRef) return;
    const ch = this.webrtcChannels.find(c => c.sessionId === this.selectedWebrtcChannel);
    this.webrtcAudioService.connect(this.selectedWebrtcChannel, this.webrtcAudioRef.nativeElement, ch?.name ?? null)
      .catch(err => console.error('[Programplan] WebRTC connect failed:', err));
  }

  onWebrtcStop(): void {
    this.webrtcAudioService.disconnect();
  }

  openVoxtralScheduleDialog(): void {
    if (!this.voxtralScheduleChannel) return;
    this.voxtralScheduleDialogVisible = true;
  }

  closeVoxtralScheduleDialog(): void {
    this.voxtralScheduleDialogVisible = false;
  }

  resetAllVoxtral(): void {
    if (!confirm('Alle Voxtral-Sessions für den ausgewählten Tag zurücksetzen? Aktive Transkriptionen werden gestoppt.')) return;

    this.voxtralResetAllInProgress = true;
    this.http.post<{ resetCount: number }>(environment.API_Base + '/broadcast/voxtral-reset-all', {
      date: this.selectedDate.toISOString(),
      channels: ''
    }).pipe(
      finalize(() => this.voxtralResetAllInProgress = false)
    ).subscribe({
      next: (result) => {
        console.log(`${result.resetCount} Voxtral-Sessions zurückgesetzt.`);
        this.loadData(this.selectedDate);
      },
      error: (err) => {
        console.error('Fehler beim Zurücksetzen aller Voxtral-Sessions:', err);
      }
    });
  }

  parseGermanDateString(dateString: string): Date | null {
    const [day, month, year] = dateString.split('.');
    if (!day || !month || !year) return null;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }

  onDateChange(value: string | Date) {
    let parsedDate: Date;

    if (typeof value === 'string') {
      const [day, month, year] = value.split('.');
      if (day && month && year) {
        parsedDate = new Date(Date.UTC(+year, +month - 1, +day));
      } else {
        parsedDate = new Date();
      }
    } else {
      // Wenn es ein Date-Objekt ist, in UTC-Midnight konvertieren
      parsedDate = new Date(Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate()
      ));
    }
  
    this.selectedDate = parsedDate;
  
    // Liste löschen und neu laden
    this.broadcasts = [];
    this.loadData(this.selectedDate);
  }

  loadData(date: Date, initializeSelectedChannels: boolean=false): void {
    this.isLoading = true; // Ladeanzeige aktivieren
    this.broadcastsService.getData('LIVE', date, CHANNEL_ORDER_CSV).subscribe({
      next: (result) => {
        this.broadcasts = JSON.parse(JSON.stringify(result.data.broadcasts));
        if( initializeSelectedChannels ) {
          this.selectedChannels = this.availableChannels;
        }
        this.calculatePositions();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Fehler beim Laden der Daten:', error);
      }
    });
  }

  private onBroadcastChanged(result: any): void {
    const data = result.data as { onBroadcastsChanged: Broadcast };
    const updatedBroadcast = data.onBroadcastsChanged;

    // Tombstone: the backend publishes the row once more with deleted=true
    // after a hard delete (DDS re-import <DeletedGuids>). Check BEFORE the
    // update branch so the ghost row is removed instead of patched.
    if (updatedBroadcast.deleted) {
      if (!this.broadcasts.some(b => b.key === updatedBroadcast.key)) {
        return; // other Programmtag / ARCHIV — nothing shown here
      }
      this.broadcasts = this.broadcasts.filter(b => b.key !== updatedBroadcast.key);
      if (this.detailsBroadcast?.key === updatedBroadcast.key) {
        this.closeDetails();
      }
      if (this.selectedBroadcast?.key === updatedBroadcast.key) {
        this.selectedBroadcast = null;
      }
      this.calculatePositions();
      this.cdr.detectChanges();
      return;
    }

    const index = this.broadcasts.findIndex(b => b.key === updatedBroadcast.key);
    if (index !== -1) {
      this.updateDifferences(this.broadcasts[index], updatedBroadcast);
      if( updatedBroadcast.key===this.detailsBroadcast?.key ) {
        this.detailsBroadcast = JSON.parse(JSON.stringify(this.broadcasts[index]));
      }
      this.calculatePositions();
    } else if (this.isInSelectedProgrammtag(updatedBroadcast)) {
      this.broadcasts = [...this.broadcasts, JSON.parse(JSON.stringify(updatedBroadcast))];
      this.calculatePositions();
      this.cdr.detectChanges();
    }
  }

  /**
   * Does a subscription payload belong to the currently displayed Programmtag?
   * Derived from the key (LIVE/yyyy/MMdd HHmm/…) exactly as the backend bakes
   * ProgrammDate (wall time − 6h). selectedDate is always UTC midnight of the
   * picked calendar day, so its ISO date IS the Programmtag date.
   */
  private isInSelectedProgrammtag(b: Broadcast): boolean {
    if (!b.key?.startsWith('LIVE/')) return false;
    if (!CHANNEL_ORDER.includes(b.channel)) return false;
    return programmtagDateFromKey(b.key) === this.selectedDate.toISOString().slice(0, 10);
  }

  updateDifferences(oldObj: any, newObj: any): void {
    const descriptors = Object.getOwnPropertyDescriptors(oldObj);
    Object.keys(newObj).forEach(key => {
      const desc = descriptors[key];
      if (desc && !desc.writable) {
        // Property ist readonly (z. B. 'key'), überspringen
        console.warn(`Property '${key}' ist readonly und kann nicht aktualisiert werden.`);
        return;
      }
  
      const oldValue = oldObj[key];
      const newValue = newObj[key];
  
      if (oldValue !== newValue) {
        console.log(`Property '${key}': ${oldValue} => ${newValue}`);
        try {
          oldObj[key] = newValue;
        } catch (err) {
          console.warn(`Konnte '${key}' nicht aktualisieren:`, err);
        }
      }
    });

    this.cdr.detectChanges();
  }
  
  calculatePositions(): void {
    this.columns = buildChannelColumns(
      this.broadcasts, this.selectedChannels, this.showLive, this.showVOD);
    this.visibleBroadcastsCount = this.columns.reduce((n, c) => n + c.broadcasts.length, 0);
  }

  trackByChannel = (_: number, col: ChannelColumn) => col.channel;
  trackByKey = (_: number, b: Broadcast) => b.key;

  getIconFilename(broadcast: { channel: string }): string {
    let extension = 'png';
    if( broadcast.channel === 'ORF1' || broadcast.channel === 'ORF2' || broadcast.channel === 'ORF3' || broadcast.channel === 'KIDS' || broadcast.channel === 'ORFS' ) {
      extension = 'svg';
    }
    let channel = broadcast.channel;
    if( channel.includes('-')) {
      const parts = channel.split('-');
      if( parts.length > 1 ) {
        channel = parts[1];
      }
    }
    return `assets/${channel}.${extension}`;
  }

  get availableChannels(): string[] {
    const unique = new Set(this.broadcasts.map(b => b.channel));
    return Array.from(unique).sort((a, z) =>
      channelSortIndex(a) - channelSortIndex(z) || a.localeCompare(z));
  }

  toggleChannel(channel: string) {
    const idx = this.selectedChannels.indexOf(channel);
    if (idx >= 0) {
      this.selectedChannels.splice(idx, 1);
    } else {
      this.selectedChannels.push(channel);
    }
    this.calculatePositions();
  }

  toggleLive() {
    this.showLive = !this.showLive;
    this.calculatePositions();
  }
  
  toggleVOD() {
    this.showVOD = !this.showVOD;
    this.calculatePositions();
  }

  get allChannelsSelected(): boolean {
    const available = this.availableChannels;
    return available.length > 0 && available.every(c => this.selectedChannels.includes(c));
  }

  toggleAllChannels() {
    if (this.allChannelsSelected) {
      this.selectedChannels = []; // alle Spalten ausblenden
    } else {
      this.selectedChannels = [...this.availableChannels]; // alle Sender aktiv
      this.showLive = true;
      this.showVOD = true;
    }
    this.calculatePositions();
  }
  
  showPopup(broadcast: any, event: MouseEvent): void {
    clearTimeout(this.hoverTimeout);  // Verhindert versehentliches Schließen
    
    const popupWidth = 300;
    const popupHeight = 700;
  
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
  
    const control = event.currentTarget as HTMLElement;
    const rect = control.getBoundingClientRect();
    
    let left = event.clientX + 10;
    let top = rect.top + 10;
  
    if (left + popupWidth > viewportWidth) {
      left = event.clientX - popupWidth - 10;
    }
  
    if (top + popupHeight > viewportHeight) {
      top = viewportHeight - popupHeight - 10;
    }

    this.selectedBroadcast = broadcast;
    this.popupPosition = { top, left };
  
    // Bei Bedarf erzwingen, dass das UI sich aktualisiert:
    this.cdr.detectChanges();
  }
  
  closePopup(): void {
    this.selectedBroadcast = null;
  }

  @HostListener('document:click')
    onDocumentClick(): void {
    this.selectedBroadcast = null;
  }

  hoverTimeout: any = null;

  onBroadcastMouseEnter(broadcast: Broadcast, event: MouseEvent): void {
    clearTimeout(this.hoverTimeout);
    this.showPopup(broadcast, event);
  }

  onBroadcastMouseLeave(): void {
    this.hoverTimeout = setTimeout(() => {
      this.closePopup();
    }, 150); // kleine Verzögerung (optional)
  }

  closeDetails() {
    this.detailsVisible = false;
    this.detailsBroadcast = null;
  }

  showDetails(broadcast: Broadcast): void {
    this.detailsBroadcast = broadcast;
    this.detailsVisible = true;
  }

  isRecording(broadcast: Broadcast): boolean {
    return !!broadcast.transcribeLiveServiceIP && broadcast.recordingStartedAt!==null && !broadcast.recordingStoppedAt;
  }

  formatPcmsStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'PendingPcmsDownload': 'PCMS Download ausstehend',
      'PcmsDownloaded': 'PCMS heruntergeladen',
      'SrtGenerated': 'SRT generiert',
      'StlGenerated': 'STL generiert',
      'SrtOffsetGenerated': 'SRT (Offset) generiert',
      'StlOffsetGenerated': 'STL (Offset) generiert',
      'FabUploaded': 'FAB hochgeladen'
    };
    return statusMap[status] || status || '—';
  }

  getPcmsTooltip(broadcast: any): string {
    const lines: string[] = [];
    if (broadcast.pcmsStatus) {
      lines.push(`Status: ${this.formatPcmsStatus(broadcast.pcmsStatus)}`);
    }
    if (broadcast.pcmsSizeInMb) {
      lines.push(`Größe: ${broadcast.pcmsSizeInMb} MB`);
    }
    if (broadcast.pcmsVideoDurationSecs) {
      const durationMins = (broadcast.pcmsVideoDurationSecs / 60).toFixed(1);
      lines.push(`Video: ${durationMins} Min`);
    }
    if (broadcast.audioDuration) {
      lines.push(`Audio: ${this.formatDuration(broadcast.audioDuration)}`);
    }
    if (broadcast.transcriptDuration) {
      lines.push(`Transkript: ${this.formatDuration(broadcast.transcriptDuration)}`);
    }
    return lines.length > 0 ? lines.join('\n') : 'PCMS';
  }

  // Format duration as mm:ss or hh:mm:ss if longer than 1 hour
  private formatDuration(seconds: number): string {
    if (!seconds) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const pad = (n: number) => n.toString().padStart(2, '0');

    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
    } else {
      return `${pad(minutes)}:${pad(secs)}`;
    }
  }
}
